import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import { API_ENDPOINTS, API_CONFIG } from '../../config/apiConfig';
import '../../pages/Settings.css';
import './SettingsInline.css';

const SettingsInline = ({ orgId, orgName = '', initialSection = 'alert-preferences' }) => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState(initialSection);
  useEffect(() => {
    setActiveSection(initialSection);
  }, [initialSection]);
  const [alertsEnabled, setAlertsEnabled] = useState(true);
  const [preferenceValues, setPreferenceValues] = useState(() => ({
    alertsMinWemq: null,
    alertsMinOemq: null,
    alertsDropPercent: null,
    alertsLookbackDays: null,
  }));
  const [expandedPreference, setExpandedPreference] = useState(null);
  const [emails, setEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailLoadError, setEmailLoadError] = useState(null);
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailSaveMessage, setEmailSaveMessage] = useState(null);
  const [preferencesSaving, setPreferencesSaving] = useState(false);
  const [preferencesMessage, setPreferencesMessage] = useState(null);
  const [lastSavedPreferences, setLastSavedPreferences] = useState({
    alertsMinWemq: null,
    alertsMinOemq: null,
    alertsDropPercent: null,
    alertsLookbackDays: null,
  });
  const readMetaConnectionStatus = () => {
    try {
      const raw = localStorage.getItem('meta_connection_status');
      if (!raw) {
        return { connected: false };
      }
      const parsed = JSON.parse(raw);
      if (parsed && parsed.connected) {
        return {
          connected: true,
          accountName: parsed.accountName || null,
          accountId: parsed.accountId || null,
          connectedAt: parsed.connectedAt || null,
          metaUserName: parsed.metaUserName || null,
          metaUserPictureUrl: parsed.metaUserPictureUrl || null,
        };
      }
    } catch (error) {
      console.warn('Unable to parse stored meta connection status:', error);
    }
    return { connected: false };
  };

  const [metaConnection, setMetaConnection] = useState(() => readMetaConnectionStatus());
  const [metaStatus, setMetaStatus] = useState({
    connected: false,
    tokenExpiresAt: null,
    metaUserName: null,
    metaUserPictureUrl: null,
  });
  const [metaStatusLoading, setMetaStatusLoading] = useState(false);
  const [metaDisconnecting, setMetaDisconnecting] = useState(false);

  const resolvedOrgId = useMemo(() => {
    const parsed =
      typeof orgId === 'number'
        ? orgId
        : typeof orgId === 'string'
        ? Number(orgId)
        : null;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [orgId]);

  const resolvedOrgName = useMemo(() => {
    if (typeof orgName !== 'string') {
      return '';
    }
    return orgName.trim();
  }, [orgName]);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const fetchPreferences = async () => {
      try {
        setLoading(true);
        setError(null);
        if (!resolvedOrgId) {
          setError('No organization is associated with this account.');
          return;
        }
        const response = await apiService.get(API_ENDPOINTS.ALERT_PREFERENCES(resolvedOrgId), {
          signal: controller.signal,
        });
        if (!isMounted) return;

        setAlertsEnabled(Boolean(response?.alertsEnabled));
        const updatedPreferences = {
          alertsMinWemq:
            typeof response?.alertsMinWemq === 'number'
              ? Number(response.alertsMinWemq)
              : null,
          alertsMinOemq:
            typeof response?.alertsMinOemq === 'number'
              ? Number(response.alertsMinOemq)
              : null,
          alertsDropPercent:
            typeof response?.alertsDropPercent === 'number'
              ? Number(response.alertsDropPercent)
              : null,
          alertsLookbackDays:
            typeof response?.alertsLookbackDays === 'number'
              ? Number(response.alertsLookbackDays)
              : null,
        };
        setPreferenceValues(updatedPreferences);
        setLastSavedPreferences(updatedPreferences);
        setPreferencesMessage(null);
      } catch (err) {
        if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') {
          return;
        }
        if (!isMounted) return;
        console.error('Failed to load alert preferences:', err);
        setError('Unable to load alert preferences right now.');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (!resolvedOrgId) {
      setLoading(false);
      return () => controller.abort();
    }

    fetchPreferences();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [resolvedOrgId]);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const fetchEmails = async () => {
      try {
        setEmailLoading(true);
        setEmailLoadError(null);
        if (!resolvedOrgId) {
          setEmailLoadError('No organization is associated with this account.');
          return;
        }
        const response = await apiService.get(API_ENDPOINTS.ALERT_EMAILS(resolvedOrgId), {
          signal: controller.signal,
        });
        if (!isMounted) return;

        const rawEmails = response?.emails;
        if (typeof rawEmails === 'string') {
          const parsed = Array.from(
            new Set(
              rawEmails
                .split(',')
                .map((email) => email.trim().toLowerCase())
                .filter(Boolean)
            )
          );
          setEmails(parsed);
        } else if (Array.isArray(response?.emails)) {
          const parsed = Array.from(
            new Set(
              response.emails
                .map((email) => (typeof email === 'string' ? email.trim().toLowerCase() : null))
                .filter(Boolean)
            )
          );
          setEmails(parsed);
        } else {
          setEmails([]);
        }
        setEmailSaveMessage(null);
      } catch (err) {
        if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') {
          return;
        }
        if (!isMounted) return;
        console.error('Failed to load alert emails:', err);
        setEmailLoadError('Unable to load alert emails right now.');
        setEmails([]);
      } finally {
        if (isMounted) {
          setEmailLoading(false);
        }
      }
    };

    if (!resolvedOrgId) {
      setEmailLoading(false);
      return () => controller.abort();
    }

    fetchEmails();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [resolvedOrgId]);

  const isMetaConnected = metaStatus?.connected && metaConnection?.connected;

  const sections = useMemo(
    () => [
      {
        id: 'alert-preferences',
        label: 'Alert Preferences',
      },
      {
        id: 'alert-emails',
        label: 'Alert Emails',
      },
      {
        id: 'meta-integration',
        label: 'Meta Integration',
      },
    ],
    [alertsEnabled, emails.length, isMetaConnected]
  );

  const togglePreferenceExpansion = (key) => {
    setExpandedPreference((prev) => (prev === key ? null : key));
  };

  const toggleAlertPreferences = () => {
    const nextStatus = !alertsEnabled;
    setAlertsEnabled(nextStatus);
    persistPreferences(
      { alertsEnabled: nextStatus },
      () => setAlertsEnabled((prev) => !prev)
    );
  };

  const persistPreferences = async (update, rollback) => {
    try {
      if (!resolvedOrgId) {
        setPreferencesMessage({
          type: 'error',
          text: 'Cannot update preferences because no organization is associated with this account.',
        });
        if (rollback) {
          rollback();
        }
        return;
      }
      setPreferencesSaving(true);
      setPreferencesMessage(null);
      await apiService.put(API_ENDPOINTS.UPDATE_ALERT_PREFERENCES(resolvedOrgId), update);
      setLastSavedPreferences((prev) => ({ ...prev, ...update }));
      setPreferencesMessage({ type: 'success', text: 'Alert preferences updated successfully.' });
    } catch (err) {
      console.error('Failed to update alert preferences:', err);
      if (rollback) {
        rollback();
      }
      setPreferencesMessage({
        type: 'error',
        text: err?.response?.data?.message || 'Failed to update alert preferences. Please try again.',
      });
    } finally {
      setPreferencesSaving(false);
    }
  };

  const updatePreferenceValue = (key, rawValue) => {
    const parsed = Number(rawValue);
    if (Number.isNaN(parsed)) {
      return;
    }

    setPreferenceValues((prev) => {
      if (!(key in prev)) {
        return prev;
      }
      const limits = {
        alertsMinWemq: { min: 0, max: 10 },
        alertsMinOemq: { min: 0, max: 10 },
        alertsDropPercent: { min: 0, max: 100 },
        alertsLookbackDays: { min: 1, max: 30 },
      };
      const { min, max } = limits[key] || { min: parsed, max: parsed };
      const clamped = Math.min(Math.max(parsed, min), max);
      return {
        ...prev,
        [key]: clamped,
      };
    });
  };

  const handlePreferenceBlur = (key) => {
    const currentValue = preferenceValues[key];
    const savedValue = lastSavedPreferences[key];
    if (currentValue === savedValue) {
      return;
    }
    const rollback = () =>
      setPreferenceValues((prev) => ({
        ...prev,
        [key]: savedValue,
      }));
    persistPreferences({ [key]: currentValue }, rollback);
  };

  const alertPreferenceItems = useMemo(
    () => [
      {
        key: 'alertsMinWemq',
        label: 'Web EMQ Threshold',
        description: 'Trigger alerts when the average web EMQ score drops below this value.',
        value: preferenceValues.alertsMinWemq,
        enabled: alertsEnabled,
        unit: '',
        min: 0,
        max: 10,
        step: 0.1,
      },
      {
        key: 'alertsMinOemq',
        label: 'Offline EMQ Threshold',
        description: 'Notify when the offline EMQ score crosses the configured boundary.',
        value: preferenceValues.alertsMinOemq,
        enabled: alertsEnabled,
        unit: '',
        min: 0,
        max: 10,
        step: 0.1,
      },
      {
        key: 'alertsDropPercent',
        label: 'Coverage Drop Percentage',
        description: 'Alert if coverage falls by the configured percentage in the defined window.',
        value: preferenceValues.alertsDropPercent,
        enabled: alertsEnabled,
        unit: '%',
        min: 0,
        max: 100,
        step: 1,
      },
      {
        key: 'alertsLookbackDays',
        label: 'Lookback Days',
        description: 'Number of days to look back when calculating alert metrics.',
        value: preferenceValues.alertsLookbackDays,
        enabled: alertsEnabled,
        unit: 'days',
        min: 1,
        max: 30,
        step: 1,
      },
    ],
    [alertsEnabled, preferenceValues]
  );

  const validateEmailList = (value) => {
    const entries = value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);

    if (!entries.length) {
      return { error: 'Email is required.', normalized: [] };
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const normalized = [];

    for (const entry of entries) {
      if (!emailPattern.test(entry)) {
        return { error: `Enter a valid email address. "${entry}" is not valid.`, normalized: [] };
      }
      const lowered = entry.toLowerCase();
      if (emails.includes(lowered) || normalized.includes(lowered)) {
        return { error: `Email "${entry}" is already added.`, normalized: [] };
      }
      normalized.push(lowered);
    }

    return { error: '', normalized };
  };

  const handleAddEmail = () => {
    const { error: validationMessage, normalized } = validateEmailList(newEmail);
    if (validationMessage) {
      setEmailError(validationMessage);
      return;
    }

    const nextEmails = [...emails, ...normalized];
    setNewEmail('');
    setEmailError('');
    persistEmails(nextEmails);
  };

  const handleRemoveEmail = (emailToRemove) => {
    const nextEmails = emails.filter((email) => email !== emailToRemove);
    persistEmails(nextEmails);
  };

  const persistEmails = async (nextEmails) => {
    try {
      if (!resolvedOrgId) {
        setEmailSaveMessage({
          type: 'error',
          text: 'Cannot update alert emails because no organization is associated with this account.',
        });
        return;
      }
      setEmailSaving(true);
      setEmailSaveMessage(null);
      const uniqueEmails = Array.from(new Set(nextEmails));
      await apiService.put(API_ENDPOINTS.UPDATE_ALERT_EMAILS(resolvedOrgId), {
        emails: uniqueEmails.join(','),
      });
      setEmails(uniqueEmails);
      setEmailSaveMessage({ type: 'success', text: 'Alert emails updated successfully.' });
    } catch (err) {
      console.error('Failed to update alert emails:', err);
      setEmailSaveMessage({
        type: 'error',
        text: err?.response?.data?.message || 'Failed to update alert emails. Please try again.',
      });
    } finally {
      setEmailSaving(false);
    }
  };

  useEffect(() => {
    const handleStorageUpdate = (event) => {
      if (!event || event.key === null || event.key === 'meta_connection_status') {
        setMetaConnection(readMetaConnectionStatus());
      }
    };
    window.addEventListener('storage', handleStorageUpdate);
    return () => window.removeEventListener('storage', handleStorageUpdate);
  }, []);

  useEffect(() => {
    if (!resolvedOrgId) {
      setMetaStatus({
        connected: false,
        tokenExpiresAt: null,
        metaUserName: null,
        metaUserPictureUrl: null,
      });
      return;
    }

    let isMounted = true;
    const controller = new AbortController();

    const fetchStatus = async () => {
      try {
        setMetaStatusLoading(true);
        const response = await apiService.get(
          API_ENDPOINTS.META_OAUTH_STATUS(resolvedOrgId),
          { signal: controller.signal }
        );
        if (!isMounted) return;
        const status = {
          connected: Boolean(response?.connected),
          tokenExpiresAt: response?.tokenExpiresAt || null,
          metaUserName: response?.metaUserName || null,
          metaUserPictureUrl: response?.metaUserPictureUrl || null,
        };
        setMetaStatus({
          connected: Boolean(status.connected),
          tokenExpiresAt: status.tokenExpiresAt,
          metaUserName: status.metaUserName || null,
          metaUserPictureUrl: status.metaUserPictureUrl || null,
        });
        setMetaConnection({
          connected: Boolean(status.connected),
          accountName: status.accountName || status.metaUserName || null,
          accountId: status.accountId || null,
          connectedAt: status.connectedAt || null,
          metaUserName: status.metaUserName || null,
          metaUserPictureUrl: status.metaUserPictureUrl || null,
        });
      } catch (err) {
        if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') {
          return;
        }
        if (!isMounted) return;
        console.error('Failed to load Meta connection status:', err);
        setMetaStatus({
          connected: false,
          tokenExpiresAt: null,
          metaUserName: null,
          metaUserPictureUrl: null,
        });
      } finally {
        if (isMounted) {
          setMetaStatusLoading(false);
        }
      }
    };

    fetchStatus();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [resolvedOrgId]);

  const handleMetaDisconnect = async () => {
    try {
      setMetaDisconnecting(true);

      if (!resolvedOrgId) {
        return;
      }

      await apiService.delete(API_ENDPOINTS.META_OAUTH_DISCONNECT(resolvedOrgId));

      try {
        localStorage.removeItem('meta_connection_status');
      } catch (err) {
        console.warn('Unable to clear stored meta connection status after disconnect:', err);
      }

      setMetaConnection({ connected: false });
      setMetaStatus({
        connected: false,
        tokenExpiresAt: null,
        metaUserName: null,
        metaUserPictureUrl: null,
      });
    } catch (err) {
      console.error('Failed to disconnect Meta account:', err);
      const fallback =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'Unable to disconnect right now. Please try again.';
    } finally {
      setMetaDisconnecting(false);
    }
  };

  return (
    <div className="settings-inline">
      <aside className="settings-inline-sidebar">
        <ul className="settings-inline-section-list">
          {sections.map((section) => {
            const isActive = activeSection === section.id;
            return (
              <li key={section.id}>
                <button
                  type="button"
                  className={`settings-inline-section ${isActive ? 'active' : ''}`}
                  onClick={() => setActiveSection(section.id)}
                >
                  <span className="settings-inline-section-label">{section.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </aside>
      <div className="settings-inline-divider" />
      <section className="settings-inline-content">
        {loading && <div className="settings-inline-banner">Loading alert settings…</div>}
        {!loading && error && <div className="settings-inline-error">{error}</div>}
        {activeSection === 'alert-preferences' && (
          <div className="settings-section">
            <div className="settings-section-header-row">
              <div>
                <h3 className="settings-section-title">Alert Preferences</h3>
                <p className="settings-section-description">
                  Configure how and when alerts fire for your pixel performance.
                </p>
              </div>
              <label className={`alert-master-toggle ${alertsEnabled ? 'enabled' : ''}`}>
                <input
                  type="checkbox"
                  checked={alertsEnabled}
                  onChange={toggleAlertPreferences}
                  aria-label={alertsEnabled ? 'Disable alert preferences' : 'Enable alert preferences'}
                  disabled={preferencesSaving}
                />
                <span className="toggle-track">
                  <span className="toggle-thumb" />
                </span>
              </label>
            </div>

            {preferencesSaving && <div className="settings-inline-banner">Saving preferences…</div>}
            {!preferencesSaving && preferencesMessage && (
              <div
                className={
                  preferencesMessage.type === 'success'
                    ? 'settings-inline-banner success'
                    : 'settings-inline-error'
                }
              >
                {preferencesMessage.text}
              </div>
            )}

            <div className="alert-preferences-list">
              {alertPreferenceItems.map((preference) => {
                const isExpanded = expandedPreference === preference.key;
                return (
                  <div
                    className={`alert-preference-card ${isExpanded ? 'expanded' : ''}`}
                    key={preference.key}
                  >
                    <button
                      type="button"
                      className="alert-preference-header"
                      onClick={() => togglePreferenceExpansion(preference.key)}
                      aria-expanded={isExpanded}
                    >
                      <div className="alert-preference-summary">
                        <span className="alert-preference-label">{preference.label}</span>
                        <span className="alert-preference-value">
                          {preference.value}
                          {preference.unit ? ` ${preference.unit}` : ''}
                        </span>
                      </div>
                      <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`} />
                    </button>

                    {isExpanded && (
                      <div className="alert-preference-body">
                        <p className="alert-preference-description">{preference.description}</p>
                        <div className="alert-preference-controls">
                          <div className="alert-preference-control">
                            <label htmlFor={`${preference.key}-value`}>Value</label>
                            <input
                              id={`${preference.key}-value`}
                              type="number"
                              min={preference.min}
                              max={preference.max}
                              step={preference.step}
                              value={preference.value}
                              onChange={(event) => updatePreferenceValue(preference.key, event.target.value)}
                              onBlur={() => handlePreferenceBlur(preference.key)}
                              disabled={!alertsEnabled || preferencesSaving}
                            />
                          </div>
                          <div className="alert-preference-control">
                            <label>Status</label>
                            <div className={`alert-state-pill ${preference.enabled ? 'enabled' : 'disabled'}`}>
                              {preference.enabled ? 'Enabled' : 'Disabled'}
                            </div>
                          </div>
                        </div>
                        {!alertsEnabled && (
                          <div className="alert-preference-disabled-note">
                            Alerts are turned off. Enable alerts to edit and activate these settings.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeSection === 'alert-emails' && (
          <div className="settings-section">
            <h3 className="settings-section-title">Alert Email Recipients</h3>
            <p className="settings-section-description">
              Manage the email addresses that should receive alert notifications. Separate multiple emails with commas.
            </p>

            {emailLoading && <div className="settings-inline-banner">Loading alert emails…</div>}
            {!emailLoading && emailLoadError && <div className="settings-inline-error">{emailLoadError}</div>}
            {!emailLoading && emailSaveMessage && (
              <div
                className={
                  emailSaveMessage.type === 'success' ? 'settings-inline-banner success' : 'settings-inline-error'
                }
              >
                {emailSaveMessage.text}
              </div>
            )}

            <div className="alert-emails-form">
              <div className="alert-email-input-group">
                <label htmlFor="alert-inline-email">Add Email(s)</label>
                <div className="alert-email-input-row">
                  <input
                    id="alert-inline-email"
                    type="text"
                    placeholder="name@example.com, teammate@company.com"
                    value={newEmail}
                    onChange={(event) => {
                      setNewEmail(event.target.value);
                      setEmailError('');
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        handleAddEmail();
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleAddEmail}
                    disabled={emailSaving || !newEmail.split(',').some((part) => part.trim())}
                  >
                    {emailSaving ? 'Saving…' : 'Add'}
                  </button>
                </div>
                {emailError && <p className="alert-email-error">{emailError}</p>}
              </div>

              <div className="alert-email-list">
                {emails.length ? (
                  emails.map((email) => (
                    <div className="alert-email-item" key={email}>
                      <div className="alert-email-meta">
                        <i className="fas fa-envelope" />
                        <span>{email}</span>
                      </div>
                      <button
                        type="button"
                        className="alert-email-remove"
                        onClick={() => handleRemoveEmail(email)}
                        disabled={emailSaving}
                        aria-label={`Remove email ${email}`}
                      >
                        Remove
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="alert-email-empty">
                    <i className="fas fa-inbox" />
                    <p>No alert recipients yet.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeSection === 'meta-integration' && (
          <div className="meta-account-table meta-account-table--compact">
            <div className="meta-account-table-header">
              <div className="meta-account-col meta-account-org">Account</div>
              <div className="meta-account-col meta-account-status">Status</div>
              <div className="meta-account-col meta-account-action">Action</div>
            </div>
            <div className="meta-account-table-row">
              <div className="meta-account-col meta-account-org">
                {metaStatusLoading ? (
                  <div className="meta-status-loading" aria-label="Loading Meta account">
                    <span className="meta-status-spinner" />
                    <span>Loading…</span>
                  </div>
                ) : isMetaConnected && (metaStatus?.metaUserName || metaConnection?.metaUserName) ? (
                  <div className="meta-account-user">
                    {(metaStatus?.metaUserPictureUrl || metaConnection?.metaUserPictureUrl) ? (
                      <img
                        src={metaStatus?.metaUserPictureUrl || metaConnection?.metaUserPictureUrl}
                        alt="Meta profile"
                        className="meta-account-user-avatar"
                      />
                    ) : (
                      <div className="meta-account-user-avatar meta-account-user-avatar--initial">
                        {(metaStatus?.metaUserName || metaConnection?.metaUserName || '—').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="meta-account-user-details">
                       <span className="meta-account-user-name">
                         {metaStatus?.metaUserName || metaConnection?.metaUserName || '—'}
                       </span>
                     </div>
                  </div>
                ) : (
                  <span className="meta-account-user-empty">No Meta account connected</span>
                )}
              </div>
              <div className="meta-account-col meta-account-status">
                {metaStatusLoading ? (
                  <div className="meta-status-loading" aria-label="Checking Meta connection">
                    <span className="meta-status-spinner" />
                    <span>Checking…</span>
                  </div>
                ) : (
                  <span className={`meta-status-chip ${isMetaConnected ? 'connected' : 'disconnected'}`}>
                    {isMetaConnected ? 'Connected' : 'Not Connected'}
                  </span>
                )}
              </div>
              <div className="meta-account-col meta-account-action">
                {metaStatusLoading ? (
                  <button type="button" className="meta-link-button connect" disabled>
                    …
                  </button>
                ) : isMetaConnected ? (
                  <button
                    type="button"
                    className="meta-link-button disconnect"
                    onClick={handleMetaDisconnect}
                    disabled={metaDisconnecting}
                  >
                    {metaDisconnecting ? 'Disconnecting…' : 'Disconnect'}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="meta-link-button connect"
                    onClick={() => navigate('/welcome')}
                  >
                    Connect
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default SettingsInline;
